<template>
  <div class="card">
    <div class="card-header py-2">
      {{ $t('visitedTiles') }}
    </div>
    <div class="card-body p-2">
      {{ $t('EPSG') }}:
      <select :disabled="epsgLoading" v-model="epsg" style="margin-right: 10px;">
        <option disabled value="epsg2180">EPSG2180</option>
        <option value="epsg3857">EPSG3857</option>
        <option value="osm">OSM</option>
      </select>
      {{ $t('size') }}:
      <select :disabled="epsgLoading" v-model="size">
        <template v-if="epsg === 'osm'">
          <option :value="1500">1500m</option>
        </template>
        <template v-else>
          <option :value="500">500m</option>
          <option :value="1000">1000m</option>
          <option :value="1500">1500m</option>
          <option :value="2000">2000m</option>
        </template>
      </select><br>
      <Spinner height="30px" v-if="epsgLoading"></Spinner>
      <template v-else-if="epsgDownloadOK">
        <b-form-checkbox class="display-inline marginright5" v-model="showTiles"></b-form-checkbox>
        <span @click="showTiles = !showTiles">{{ $t('showVisitedTiles') }}</span><br>
        <b-form-checkbox class="display-inline marginright5" v-model="showAllTiles"></b-form-checkbox>
        <span @click="showAllTiles = !showAllTiles">{{ $t('showAllTiles') }}</span><br><br>
        <b-form-checkbox class="display-inline marginright5" v-model="linkBorderAndFillColor"></b-form-checkbox>
        <span>Link border and fill color</span><br><br>
        <div style="display: flex; gap: 10px; align-items: center; flex-wrap: wrap;">
          <div>
            <div 
              :id="'colorfill-neighbors'" 
              :style="{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: colorVisitedWithNeighborsFillRgba, cursor: linkBorderAndFillColor ? 'default' : 'pointer', border: '2px solid #ccc', opacity: linkBorderAndFillColor ? 0.6 : 1 }"
            ></div>
            <b-popover v-if="!linkBorderAndFillColor" :target="'colorfill-neighbors'" triggers="focus" placement="top">
              <template v-slot:title>Visited tiles with neighbors (fill)</template>
              <SketchPicker v-model="colorVisitedWithNeighborsFill" :disableAlpha="false"></SketchPicker>
            </b-popover>
          </div>
          <div>
            <div 
              :id="'colorstroke-neighbors'" 
              :style="{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: colorVisitedWithNeighborsStrokeRgba, cursor: 'pointer', border: '2px solid #ccc' }"
            ></div>
            <b-popover :target="'colorstroke-neighbors'" triggers="focus" placement="top">
              <template v-slot:title>Visited tiles with neighbors (stroke)</template>
              <SketchPicker v-model="colorVisitedWithNeighborsStroke" :disableAlpha="false"></SketchPicker>
            </b-popover>
          </div>
          <div>
            <div 
              :id="'colorfill-boundary'" 
              :style="{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: colorVisitedBoundaryFillRgba, cursor: linkBorderAndFillColor ? 'default' : 'pointer', border: '2px solid #ccc', opacity: linkBorderAndFillColor ? 0.6 : 1 }"
            ></div>
            <b-popover v-if="!linkBorderAndFillColor" :target="'colorfill-boundary'" triggers="focus" placement="top">
              <template v-slot:title>Visited boundary tiles (fill)</template>
              <SketchPicker v-model="colorVisitedBoundaryFill" :disableAlpha="false"></SketchPicker>
            </b-popover>
          </div>
          <div>
            <div 
              :id="'colorstroke-boundary'" 
              :style="{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: colorVisitedBoundaryStrokeRgba, cursor: 'pointer', border: '2px solid #ccc' }"
            ></div>
            <b-popover :target="'colorstroke-boundary'" triggers="focus" placement="top">
              <template v-slot:title>Visited boundary tiles (stroke)</template>
              <SketchPicker v-model="colorVisitedBoundaryStroke" :disableAlpha="false"></SketchPicker>
            </b-popover>
          </div>
          <div>
            <div 
              :id="'colorstroke-unvisited'" 
              :style="{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: colorUnvisitedStrokeRgba, cursor: 'pointer', border: '2px solid #ccc' }"
            ></div>
            <b-popover :target="'colorstroke-unvisited'" triggers="focus" placement="top">
              <template v-slot:title>Unvisited tiles (stroke)</template>
              <SketchPicker v-model="colorUnvisitedStroke" :disableAlpha="false"></SketchPicker>
            </b-popover>
          </div>
          <button @click="resetColors" class="btn btn-sm btn-outline-secondary" style="padding: 2px 8px; font-size: 12px;">Reset</button>
        </div>
        <b-form-checkbox class="display-inline marginright5" v-model="showOnlySelectedTracks"></b-form-checkbox>
        <span @click="showOnlySelectedTracks = !showOnlySelectedTracks">{{ $t('showOnlySelectedTracks') }}</span>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import BaseComponent from '@/components/Base.vue';
import { VisitedTilesLayer } from '@/ts/VisitedTilesLayer';
import { defineComponent, markRaw } from "vue";
import { AlertStatus, DownloadStatus } from '@/ts/types';
import Spinner from '../Spinner.vue';
import { EventBus } from '@/ts/EventBus';
import proj4 from 'proj4';
import { openGridDirectory, saveGridToCache, loadGridFromCache, checkGridInCache } from '@/ts/utils/opfs';
import { error as debugError } from '@/ts/debug';

export default defineComponent({
  extends: BaseComponent,
  data() {
    return {
      showTiles: true,
      showAllTiles: false,
      showOnlySelectedTracks: false,
      linkBorderAndFillColor: false,
      layer: null as VisitedTilesLayer,
      epsgLoading: false,
      epsg: '',
      size: 0,
      epsgDownloadOK: false,
      gridsCache: {} as Record<string, any>,
      colorVisitedWithNeighborsFill: { r: 144, g: 238, b: 144, a: 0.5 },
      colorVisitedWithNeighborsStroke: { r: 0, g: 100, b: 0, a: 1 },
      colorVisitedBoundaryFill: { r: 255, g: 100, b: 100, a: 0.5 },
      colorVisitedBoundaryStroke: { r: 139, g: 0, b: 0, a: 1 },
      colorUnvisitedStroke: { r: 135, g: 206, b: 235, a: 1 },
    };
  },
  methods: {
    projectToPixels(lat: number, lng: number): [number, number] {
      const sinLat = Math.sin((lat * Math.PI) / 180);
      const x = ((lng + 180) / 360) * 256;
      const y = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * 256;
      return [x, y];
    },

    createGrid(startCoords: [number, number], endCoords: [number, number], crscode: string, stepSize: number): any {
      // Definiuj transformacje proj4
      proj4.defs("EPSG:2180", "+proj=tmerc +lat_0=0 +lon_0=19 +k=0.9993 +x_0=500000 +y_0=-5300000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs");
      
      const toCrs = proj4('EPSG:4326', crscode);

      const [startLat, startLon] = startCoords;
      const [endLat, endLon] = endCoords;

      // Transformacja punktów brzegowych
      const [xStart, yStart] = toCrs.forward([startLon, startLat]);
      const [xEnd, yEnd] = toCrs.forward([endLon, endLat]);

      // Liczba kroków (kafli)
      const nx = Math.ceil((xEnd - xStart) / stepSize);
      const ny = Math.ceil((yEnd - yStart) / stepSize);

      // Budowanie gridu z współrzędnymi
      const grid = [];
      for (let i = 0; i < nx; i++) {
        const column = [];
        for (let j = 0; j < ny; j++) {
          const x = xStart + i * stepSize;
          const y = yStart + j * stepSize;

          // Wierzchołki: lewy-dół, lewy-góra, prawy-góra, prawy-dół
          const verticesPlanar = [
            [x, y],
            [x, y + stepSize],
            [x + stepSize, y + stepSize],
            [x + stepSize, y]
          ];

          const tilePixels = [];
          for (const [vx, vy] of verticesPlanar) {
            const [lon, lat] = toCrs.inverse([vx, vy]);
            const [pixelX, pixelY] = this.projectToPixels(lat, lon);
            tilePixels.push([pixelX, pixelY]);
          }

          column.push(tilePixels);
        }
        grid.push(column);
      }

      return grid;
    },

    async ensureGridLoaded(epsg: string, size: number): Promise<any> {
      // Use UI key naming `${epsg}_${size}` so backend and cache align (e.g. 'osm_1500')
      const key = `${epsg}_${size}`;
      
      // Check if already in memory cache
      if (this.gridsCache[key]) {
        return this.gridsCache[key];
      }

      // Try to load from OPFS cache
      const cacheFileName = `grid_${key}.bin`;
      const gridData = await loadGridFromCache(cacheFileName);
      if (gridData) {
        markRaw(gridData);
        this.gridsCache[key] = gridData;
        return gridData;
      }

      // Generate grid and save to OPFS
      const START_PT: [number, number] = [49.002380, 14.122222];
      const END_PT: [number, number] = [54.836389, 24.154167];
      const OSM_START_PT = [48.99463598, 14.10644531]
      const OSM_END_PT = [54.83866361, 24.16992188]
      const OSM_MERCATOR_STEP = 2445.98437535

      const getMercatorStep = (sizeVal: number) => {
        const refLatRad = (52.8 * Math.PI) / 180;
        return sizeVal / Math.cos(refLatRad);
      };

      const crsMap: Record<string, string> = {
        'epsg2180': 'EPSG:2180',
        'epsg3857': 'EPSG:3857'
      };

      const stepSizeMap: Record<string, number> = {
        'epsg2180_500': 500,
        'epsg2180_1000': 1000,
        'epsg2180_1500': 1500,
        'epsg2180_2000': 2000,
        'epsg3857_500': getMercatorStep(500),
        'epsg3857_1000': getMercatorStep(1000),
        'epsg3857_1500': getMercatorStep(1500),
        'epsg3857_2000': getMercatorStep(2000),
      };

      // If epsg==='osm' we must build grid using OSM_START_PT/END_PT and mercator step
      let newGrid;
      if (epsg === 'osm') {
        newGrid = this.createGrid(OSM_START_PT, OSM_END_PT, 'EPSG:3857', OSM_MERCATOR_STEP);
      } else {
        const crs = crsMap[epsg];
        const stepSize = stepSizeMap[`${epsg}_${size}`];
        newGrid = this.createGrid(START_PT, END_PT, crs, stepSize);
      }
      markRaw(newGrid);
      this.gridsCache[key] = newGrid;

      // Save to OPFS asynchronously (don't wait for it)
      saveGridToCache(cacheFileName, newGrid).catch(err => {
        debugError('Failed to save grid to cache:', err);
      });

      return newGrid;
    },

    downloadData() {
      this.epsgDownloadOK = false;
      this.epsgLoading = true;
      if (this.showTiles) {
        this.layer.removeFrom(window.mainmap);
      }

      const key = `${this.epsg}_${this.size}`;
      const bounds = this.gridsCache[key];

      if (!bounds || bounds.length === 0) {
        this.createAlert(AlertStatus.danger, this.$t('girdError'), 2000);
        this.epsgLoading = false;
        return;
      }

      this.layer.setBounds(bounds);

      // Pobierz rzeczywiste wartości z API
      const url = window.apiHost + 'api/stringfield/grid_' + this.epsg + '_' + this.size + '/';
      
      const grid_key = 'grid_' + this.epsg + '_' + this.size
      this.downloadUrlWithCache(url, grid_key, grid_key + '.txt', null, true).then((response) => {
        let valuesData = response[1].results;
        this.layer.setValues(valuesData);
        if (this.showTiles) {
          this.layer.addTo(window.mainmap);
        }
        this.epsgDownloadOK = true;
        if (response[0] == DownloadStatus.cache_fresh) {
          this.createAlert(AlertStatus.success, this.$t('visitedTilesLoaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.api_fresh) {
          this.createAlert(AlertStatus.success, this.$t('visitedTilesDownloaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.cache_last) {
          this.createAlert(AlertStatus.warning, this.$t('visitedTilesLoaded', [response[1].results.length]).toString(), 2000);
        }
      }).catch(() => {
        this.epsgDownloadOK = false;
        this.createAlert(AlertStatus.danger, this.$t('visitedTilesError').toString(), 2000);
      }).finally(() => {
        this.epsgLoading = false;
      })
    },

    normalizeColor(color: any): any {
      // If SketchPicker returned a full object with rgba property, extract it
      if (color && color.rgba && typeof color.rgba === 'object') {
        return color.rgba;
      }
      // If already in our format, return as is
      if (color && color.r !== undefined && color.g !== undefined && color.b !== undefined) {
        return color;
      }
      // Default fallback
      return { r: 0, g: 0, b: 0, a: 1 };
    },

    updateLayerColors(): void {
      const colorToRgbaString = (color: any): string => {
        if (typeof color === 'string') {
          return color;
        }
        // Handle SketchPicker format with rgba property
        if (color && color.rgba && typeof color.rgba === 'object') {
          const { r, g, b, a } = color.rgba;
          return `rgba(${r}, ${g}, ${b}, ${a})`;
        }
        // Handle direct RGBA format
        if (color.r !== undefined && color.g !== undefined && color.b !== undefined && color.a !== undefined) {
          return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
        }
        return 'rgba(0, 0, 0, 1)';
      };

      this.layer.setVisitedWithNeighborsFill(colorToRgbaString(this.colorVisitedWithNeighborsFill));
      this.layer.setVisitedWithNeighborsStroke(colorToRgbaString(this.colorVisitedWithNeighborsStroke));
      this.layer.setVisitedBoundaryFill(colorToRgbaString(this.colorVisitedBoundaryFill));
      this.layer.setVisitedBoundaryStroke(colorToRgbaString(this.colorVisitedBoundaryStroke));
      this.layer.setUnvisitedStroke(colorToRgbaString(this.colorUnvisitedStroke));

      // Save colors to localStorage
      this.saveColorsToLocalStorage();
    },

    updateSelectedTracksFilter(): void {
      this.layer.setShowOnlySelectedTracks(this.showOnlySelectedTracks);
      this.layer.setSelectedTracks(this.showOnlySelectedTracks ? this.selectedTracks : []);
    },

    saveColorsToLocalStorage(): void {
      const extractRgba = (color: any) => {
        if (color && color.rgba) {
          return color.rgba;
        }
        return color;
      };

      const colors = {
        linkBorderAndFillColor: this.linkBorderAndFillColor,
        colorVisitedWithNeighborsFill: extractRgba(this.colorVisitedWithNeighborsFill),
        colorVisitedWithNeighborsStroke: extractRgba(this.colorVisitedWithNeighborsStroke),
        colorVisitedBoundaryFill: extractRgba(this.colorVisitedBoundaryFill),
        colorVisitedBoundaryStroke: extractRgba(this.colorVisitedBoundaryStroke),
        colorUnvisitedStroke: extractRgba(this.colorUnvisitedStroke),
      };
      localStorage.setItem('visitedTilesColors', JSON.stringify(colors));
    },

    loadColorsFromLocalStorage(): void {
      try {
        const stored = localStorage.getItem('visitedTilesColors');
        if (stored) {
          const colors = JSON.parse(stored);
          this.linkBorderAndFillColor = colors.linkBorderAndFillColor ?? false;
          this.colorVisitedWithNeighborsFill = colors.colorVisitedWithNeighborsFill;
          this.colorVisitedWithNeighborsStroke = colors.colorVisitedWithNeighborsStroke;
          this.colorVisitedBoundaryFill = colors.colorVisitedBoundaryFill;
          this.colorVisitedBoundaryStroke = colors.colorVisitedBoundaryStroke;
          this.colorUnvisitedStroke = colors.colorUnvisitedStroke;
          this.updateLayerColors();
        }
      } catch (e) {
        debugError('Error loading colors from localStorage:', e);
      }
    },

    resetColors(): void {
      this.colorVisitedWithNeighborsFill = { r: 144, g: 238, b: 144, a: 0.5 };
      this.colorVisitedWithNeighborsStroke = { r: 0, g: 100, b: 0, a: 1 };
      this.colorVisitedBoundaryFill = { r: 255, g: 100, b: 100, a: 0.5 };
      this.colorVisitedBoundaryStroke = { r: 139, g: 0, b: 0, a: 1 };
      this.colorUnvisitedStroke = { r: 135, g: 206, b: 235, a: 1 };
      localStorage.removeItem('visitedTilesColors');
      this.updateLayerColors();
    }
  },
  watch: {
    "showTiles": function() {
      this.layer.setShowVisitedTiles(this.showTiles);
      if (this.showTiles) {
        this.layer.addTo(window.mainmap);
      } else if (!this.showAllTiles) {
        // Only remove if both showTiles and showAllTiles are false
        this.layer.removeFrom(window.mainmap);
      }
    },
    "showAllTiles": function() {
      this.layer.setShowAllTiles(this.showAllTiles);
      // If showAllTiles is enabled but layer is not on map, add it
      if (this.showAllTiles && !this.showTiles && !this.layer._map) {
        this.layer.addTo(window.mainmap);
      }
      // If both are disabled, remove layer
      if (!this.showAllTiles && !this.showTiles) {
        this.layer.removeFrom(window.mainmap);
      }
    },
    "showOnlySelectedTracks": function() {
      this.updateSelectedTracksFilter();
    },
    "selectedTrackIds": {
      handler() {
        if (this.showOnlySelectedTracks) {
          this.updateSelectedTracksFilter();
        }
      }
    },
    "size": async function() {
      if (this.size && this.epsg) {
        await this.ensureGridLoaded(this.epsg, this.size);
        this.downloadData();
      }
    },
    "epsg": async function() {
      if (this.epsg === 'osm') {
        // enforce single allowed size for OSM
        if (this.size && this.size !== 1500) this.size = 1500;
      }
      if (this.size && this.epsg) {
        await this.ensureGridLoaded(this.epsg, this.size);
        this.downloadData();
      }
    },
    "linkBorderAndFillColor": {
      handler() {
        this.updateLayerColors();
      }
    },
    "colorVisitedWithNeighborsFill": {
      handler() {
        this.updateLayerColors();
      },
      deep: true
    },
    "colorVisitedWithNeighborsStroke": {
      handler() {
        // When linked, synchronize fill color from stroke with 0.25 alpha
        if (this.linkBorderAndFillColor) {
          const stroke: any = this.normalizeColor(this.colorVisitedWithNeighborsStroke);
          const a = (stroke && stroke.a !== undefined) ? stroke.a : 1;
          this.colorVisitedWithNeighborsFill = {
            r: stroke?.r ?? 0,
            g: stroke?.g ?? 0,
            b: stroke?.b ?? 0,
            a: Math.max(0, Math.min(1, a * 0.25))
          };
        }
        this.updateLayerColors();
      },
      deep: true
    },
    "colorVisitedBoundaryFill": {
      handler() {
        this.updateLayerColors();
      },
      deep: true
    },
    "colorVisitedBoundaryStroke": {
      handler() {
        // When linked, synchronize fill color from stroke with 0.25 alpha
        if (this.linkBorderAndFillColor) {
          const stroke: any = this.normalizeColor(this.colorVisitedBoundaryStroke);
          const a = (stroke && stroke.a !== undefined) ? stroke.a : 1;
          this.colorVisitedBoundaryFill = {
            r: stroke?.r ?? 0,
            g: stroke?.g ?? 0,
            b: stroke?.b ?? 0,
            a: Math.max(0, Math.min(1, a * 0.25))
          };
        }
        this.updateLayerColors();
      },
      deep: true
    },
    "colorUnvisitedStroke": {
      handler() {
        this.updateLayerColors();
      },
      deep: true
    }
  },
  computed: {
    colorVisitedWithNeighborsFillRgba(): string {
      const color: any = this.colorVisitedWithNeighborsFill;
      if (typeof color === 'string') return color;
      // Handle SketchPicker format
      if (color && color.rgba && typeof color.rgba === 'object') {
        const { r, g, b, a } = color.rgba;
        return `rgba(${r}, ${g}, ${b}, ${a})`;
      }
      // Handle direct RGBA format
      if (color && color.r !== undefined) {
        return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
      }
      return '#000000';
    },
    colorVisitedWithNeighborsStrokeRgba(): string {
      const color: any = this.colorVisitedWithNeighborsStroke;
      if (typeof color === 'string') return color;
      // Handle SketchPicker format
      if (color && color.rgba && typeof color.rgba === 'object') {
        const { r, g, b, a } = color.rgba;
        return `rgba(${r}, ${g}, ${b}, ${a})`;
      }
      // Handle direct RGBA format
      if (color && color.r !== undefined) {
        return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
      }
      return '#000000';
    },
    colorVisitedBoundaryFillRgba(): string {
      const color: any = this.colorVisitedBoundaryFill;
      if (typeof color === 'string') return color;
      // Handle SketchPicker format
      if (color && color.rgba && typeof color.rgba === 'object') {
        const { r, g, b, a } = color.rgba;
        return `rgba(${r}, ${g}, ${b}, ${a})`;
      }
      // Handle direct RGBA format
      if (color && color.r !== undefined) {
        return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
      }
      return '#000000';
    },
    colorVisitedBoundaryStrokeRgba(): string {
      const color: any = this.colorVisitedBoundaryStroke;
      if (typeof color === 'string') return color;
      // Handle SketchPicker format
      if (color && color.rgba && typeof color.rgba === 'object') {
        const { r, g, b, a } = color.rgba;
        return `rgba(${r}, ${g}, ${b}, ${a})`;
      }
      // Handle direct RGBA format
      if (color && color.r !== undefined) {
        return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
      }
      return '#000000';
    },
    colorUnvisitedStrokeRgba(): string {
      const color: any = this.colorUnvisitedStroke;
      if (typeof color === 'string') return color;
      // Handle SketchPicker format
      if (color && color.rgba && typeof color.rgba === 'object') {
        const { r, g, b, a } = color.rgba;
        return `rgba(${r}, ${g}, ${b}, ${a})`;
      }
      // Handle direct RGBA format
      if (color && color.r !== undefined) {
        return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
      }
      return '#000000';
    },
    selectedTracks(): any[] {
      return this.$store.getters.selectedTracks;
    },
    selectedTrackIds(): number[] {
      if (!this.showOnlySelectedTracks) {
        return [];
      }
      return this.selectedTracks.map(track => track.id);
    }
  },
  async mounted() {
    const layer = new VisitedTilesLayer();
    markRaw(layer)
    this.layer = layer;
    
    // Initialize grid directory for caching
    await openGridDirectory();

    // Load colors from localStorage
    this.loadColorsFromLocalStorage();
    this.layer.setShowVisitedTiles(this.showTiles);
    this.updateSelectedTracksFilter();

    EventBus.$on('showOSMVisitedTiles', () => {
      this.showTiles = true;
      this.epsg = 'osm';
      this.size = 1500;
    });
    EventBus.$on('showEPSG3857VisitedTiles', () => {
      this.showTiles = true;
      this.epsg = 'epsg3857';
      this.size = 1000;
    });
    EventBus.$on('toggleVisitedTilesAllLines', () => {
      this.showAllTiles = !this.showAllTiles;
    });
    EventBus.$on('hideVisitedTiles', () => {
      this.showAllTiles = false;
      this.showTiles = false;
    });
  }
});

</script>